/**
 * Recurring Transactions Manager
 * 
 * Gerencia lançamentos recorrentes - permite visualizar, editar e cancelar
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { 
  Repeat, 
  Calendar, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  ArrowLeftRight,
  Trash2,
  Edit,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { getFrequencyLabel } from '../../../lib/utils/recurring';
import { formatCurrency } from '../../../lib/utils/format';
import { useCompanyCurrency } from '../../../hooks/useCompanyCurrency';
import { notify } from '../../../lib/notifications';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import type { CashFlowEntry } from '../../../types/database';

interface RecurringTransactionsManagerProps {
  companyId: string;
  userId: string;
  onEdit?: (transaction: CashFlowEntry) => void;
}

interface RecurringGroup {
  template: CashFlowEntry;
  occurrences: CashFlowEntry[];
  totalOccurrences: number;
  completedOccurrences: number;
}

export function RecurringTransactionsManager({
  companyId,
  userId,
  onEdit,
}: RecurringTransactionsManagerProps) {
  const companyCurrency = useCompanyCurrency();
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Fetch all recurring transactions
  const { data: recurringGroups = [], isLoading } = useQuery({
    queryKey: ['recurring-transactions', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cash_flow_entries')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_recurring', true)
        .order('entry_date', { ascending: true });

      if (error) throw error;

      // Group by description + frequency + amount (same recurring series)
      const groups = new Map<string, RecurringGroup>();
      
      data.forEach((entry) => {
        const key = `${entry.description}-${entry.frequency}-${entry.amount}`;
        
        if (!groups.has(key)) {
          groups.set(key, {
            template: entry,
            occurrences: [],
            totalOccurrences: 0,
            completedOccurrences: 0,
          });
        }
        
        const group = groups.get(key)!;
        group.occurrences.push(entry);
        group.totalOccurrences++;
        
        if (entry.payment_status === 'confirmado') {
          group.completedOccurrences++;
        }
      });

      return Array.from(groups.values());
    },
    enabled: !!companyId,
  });

  // Delete all occurrences of a recurring series
  const deleteMutation = useMutation({
    mutationFn: async (occurrences: CashFlowEntry[]) => {
      const ids = occurrences.map(o => o.id);
      
      const { error } = await supabase
        .from('cash_flow_entries')
        .delete()
        .in('id', ids);

      if (error) throw error;
    },
    onSuccess: () => {
      notify.success('Recorrência cancelada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['recurring-transactions', companyId] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      setDeletingId(null);
    },
    onError: (error: Error) => {
      notify.error('Erro ao cancelar recorrência', error.message);
      setDeletingId(null);
    },
  });

  const getEntryTypeIcon = (type: string) => {
    switch (type) {
      case 'income': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'expense': return <TrendingDown className="h-4 w-4 text-red-600" />;
      case 'transfer': return <ArrowLeftRight className="h-4 w-4 text-blue-600" />;
      default: return null;
    }
  };

  const getEntryTypeLabel = (type: string) => {
    switch (type) {
      case 'income': return 'Receita';
      case 'expense': return 'Despesa';
      case 'transfer': return 'Transferência';
      default: return type;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (recurringGroups.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-12">
            <Repeat className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhuma recorrência ativa</h3>
            <p className="text-sm text-muted-foreground">
              Crie um lançamento recorrente para gerenciá-lo aqui
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {recurringGroups.map((group, index) => (
          <Card key={`${group.template.id}-${index}`} className="overflow-hidden">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getEntryTypeIcon(group.template.entry_type)}
                    <CardTitle className="text-lg">
                      {group.template.description}
                    </CardTitle>
                  </div>
                  <CardDescription className="flex items-center gap-4 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Repeat className="h-3 w-3" />
                      {getFrequencyLabel(group.template.frequency!)}
                    </span>
                    <span className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      {formatCurrency(group.template.amount, companyCurrency)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {group.totalOccurrences} ocorrências
                    </span>
                  </CardDescription>
                </div>

                <div className="flex gap-2">
                  {onEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(group.template)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeletingId(group.template.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent>
              <div className="space-y-3">
                {/* Progress */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Progresso</span>
                    <span className="font-medium">
                      {group.completedOccurrences} / {group.totalOccurrences} confirmados
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 transition-all"
                      style={{
                        width: `${(group.completedOccurrences / group.totalOccurrences) * 100}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Category and Date Range */}
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">
                    {group.template.main_category} → {group.template.category}
                  </Badge>
                  <Badge variant="secondary">
                    {getEntryTypeLabel(group.template.entry_type)}
                  </Badge>
                  {group.occurrences.length > 0 && (
                    <Badge variant="outline" className="text-xs">
                      {new Date(group.occurrences[0].entry_date!).toLocaleDateString('pt-BR')} -{' '}
                      {new Date(group.occurrences[group.occurrences.length - 1].entry_date!).toLocaleDateString('pt-BR')}
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingId} onOpenChange={() => setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Cancelar Recorrência
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Tem certeza que deseja cancelar esta recorrência?
              </p>
              <p className="font-medium">
                Isso irá excluir TODOS os {
                  recurringGroups.find(g => g.template.id === deletingId)?.totalOccurrences || 0
                } lançamentos desta série.
              </p>
              <p className="text-destructive text-sm">
                Esta ação não pode ser desfeita.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const group = recurringGroups.find(g => g.template.id === deletingId);
                if (group) {
                  deleteMutation.mutate(group.occurrences);
                }
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Sim, excluir todos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

